/**
 * Route Builder Utility
 *
 * Converts declarative route configuration into React Router RouteObject[]
 */

import { Suspense, ReactElement, ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import { RouteConfig } from 'src/core/types/appConfig';
import { CoreLayout, CoreComponent } from 'src/core/routes';

/**
 * Layout resolver function type
 * Maps layout enum or import paths to React component constructors
 */
export type LayoutResolver = (layoutPath: string | CoreLayout) => ComponentType<any>;

/**
 * Component import resolver function type
 * Maps component enum or import paths to lazy-loaded components
 * This allows webpack to statically analyze all imports for code splitting
 */
export type ComponentResolver = (componentPath: string | CoreComponent | string | number) => React.LazyExoticComponent<ComponentType<any>>;

/**
 * Component loader HOC for lazy loading
 */
const createLoader = (SuspenseFallback: ComponentType<any>) =>
  (Component: ComponentType<any>) => (props: any) => (
    <Suspense fallback={<SuspenseFallback />}>
      <Component {...props} />
    </Suspense>
  );

/**
 * Builds RouteObject[] from route configuration
 *
 * @param config - Array of route configurations
 * @param options - Configuration options
 * @returns Array of RouteObject compatible with React Router
 */
export function buildRoutes(
  config: RouteConfig[],
  options: {
    layoutResolver: LayoutResolver;
    componentResolver: ComponentResolver;
    PrivateRoute: (props: { children: React.ReactNode }) => ReactElement;
    RedirectIfAuthorized?: (props: { children: React.ReactNode }) => ReactElement;
    SuspenseLoader: ComponentType<any>;
  }
): RouteObject[] {
  const { layoutResolver, componentResolver, PrivateRoute, RedirectIfAuthorized, SuspenseLoader } = options;
  const Loader = createLoader(SuspenseLoader);

  const buildRoute = (routeConfig: RouteConfig): RouteObject => {
    const route: RouteObject = {
      path: routeConfig.path,
    };

    // Handle different route types
    switch (routeConfig.type) {
      case 'page': {
        // Get lazy-loaded component from resolver
        const LazyComponent = componentResolver(routeConfig.component);
        const LoadedComponent = Loader(LazyComponent);

        let element: ReactElement = <LoadedComponent />;

        // Wrap with auth guard if private
        if (routeConfig.private) {
          element = <PrivateRoute>{element}</PrivateRoute>;
        } else if (routeConfig.redirectIfAuthorized && RedirectIfAuthorized) {
          // Wrap routes that should redirect if already authorized (e.g., login/register)
          element = <RedirectIfAuthorized>{element}</RedirectIfAuthorized>;
        }

        route.element = element;
        break;
      }

      case 'redirect': {
        let element: ReactElement = <Navigate to={routeConfig.redirectTo} replace />;

        // Wrap with auth guard if private
        if (routeConfig.private) {
          element = <PrivateRoute>{element}</PrivateRoute>;
        }

        route.element = element;
        break;
      }

      case 'group': {
        // Set layout element if specified
        if (routeConfig.layout) {
          const Layout = layoutResolver(routeConfig.layout);
          // Pass menuItems to layout if available
          route.element = <Layout menuItems={routeConfig.menuItems} />;
        }

        // Recursively build children routes
        if (routeConfig.children && routeConfig.children.length > 0) {
          route.children = routeConfig.children.map(child => buildRoute(child));
        }

        break;
      }
    }

    return route;
  };

  return config.map(route => buildRoute(route));
}
